import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'hs-lodash/isEqual';
import omit from 'transmute/omit';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useStore } from 'framework-listing-lib/internal/store/zustand';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import useCurrentView from 'framework-listing-lib/hooks/useCurrentView';
import { updateHiddenFilterForCalendarDisplayDate } from 'framework-listing-lib/internal/Calendar/utils/filters';
import { updateHiddenFilterForFolder } from 'framework-listing-lib/internal/Folders/utils/filters';
import { useCurrentViewId, useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { updateFilterGroupsForBUNav, updateHiddenFilterForBUNav, updateQuickFiltersForBUNav } from 'framework-listing-lib/internal/businessUnits/utils';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useDeepEqual from 'framework-listing-lib/internal/hooks/useDeepEqual';
import { useEventCallback } from 'framework-listing-lib/internal/hooks/useEventCallback';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { createFilterStore, filtersStateSelectors } from '../store/filterStateStore';
import { areSameFilterGroups, getAggregatedFilterGroups } from 'framework-listing-lib/utils/filters';
import useSyncFiltersWithSessionStorage from './useSyncFiltersWithSessionStorage';
import { useCurrentUIMode } from './useUIMode';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getViewId } from '../ViewTabs/utils/views';

/**
 * This hook is meant to be used only by CrmObjectTypeListingClientProvider.
 * It initialises the global filter state used by all hooks & components in this codebase.
 *
 * We update filters from this hook in two scenarios:
 *  - when @initialFilter or @objectType prop changes.
 *  - when the current view changes.
 *
 * This hooks has & controls:
 *  - list of deleted object ids (due to the delay with the CRM, we manually omit deleted objects when sending request to crm-search).
 *  - filters that have been applied to the UI
 */
export default function useCrmObjectTypeListingFilterState(t0) {
  "use memo";

  const $ = _c(94);
  const {
    calendarProps,
    hiddenFilter: hiddenFilterProp,
    initialFilter: initialFilterProp,
    folderId
  } = t0;
  const urlParams = useHandleQueryParams();
  const currentUIMode = useCurrentUIMode();
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewId = useCurrentViewId();
  const initialFilter = useDeepEqual(initialFilterProp);
  const hasBUNavIntegration = useHasBUNavIntegration();
  const objectType = useObjectTypeId();
  let t1;
  if ($[0] !== currentUIMode || $[1] !== currentView || $[2] !== initialFilter) {
    t1 = () => {
      const store = createFilterStore();
      store.getState().initializeFiltersState({
        currentView,
        uiMode: currentUIMode,
        initialFilter
      });
      return store;
    };
    $[0] = currentUIMode;
    $[1] = currentView;
    $[2] = initialFilter;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [filtersStateStore] = useState(t1);
  const t2 = useStore(filtersStateStore, _temp);
  const t3 = useStore(filtersStateStore, _temp2);
  const t4 = !useStore(filtersStateStore, _temp3);
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      filterGroups: t2,
      quickFilters: t3,
      skip: t4
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const filterStateFromSessionStorage = useSyncFiltersWithSessionStorage(t5);
  const [prevViewId, setPrevViewId] = useState(undefined);
  let t6;
  if ($[8] !== currentUIMode || $[9] !== currentView || $[10] !== currentViewId || $[11] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) || $[12] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) || $[13] !== filtersStateStore || $[14] !== initialFilter || $[15] !== objectType || $[16] !== prevViewId) {
    t6 = () => {
      var _filterStateFromSessi, _filterStateFromSessi2;
      if (currentUIMode === UI_MODE.FOLDERS || !currentView) {
        setPrevViewId(undefined);
        return;
      }
      const viewId = getViewId(currentView);
      if (prevViewId === viewId) {
        return;
      }
      setPrevViewId(viewId);
      const resolvedView = !currentView || !currentViewId || objectType !== currentView.objectTypeId ? undefined : Object.assign({}, currentView, {
        filterGroups: (_filterStateFromSessi = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) !== null && _filterStateFromSessi !== void 0 ? _filterStateFromSessi : currentView === null || currentView === void 0 ? void 0 : currentView.filterGroups,
        quickFilters: (_filterStateFromSessi2 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) !== null && _filterStateFromSessi2 !== void 0 ? _filterStateFromSessi2 : currentView === null || currentView === void 0 ? void 0 : currentView.quickFilters
      });
      filtersStateStore.getState().initializeFiltersState({
        currentView: resolvedView,
        uiMode: currentUIMode,
        initialFilter
      });
    };
    $[8] = currentUIMode;
    $[9] = currentView;
    $[10] = currentViewId;
    $[11] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
    $[12] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
    $[13] = filtersStateStore;
    $[14] = initialFilter;
    $[15] = objectType;
    $[16] = prevViewId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
  const t8 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
  let t9;
  if ($[18] !== currentUIMode || $[19] !== currentView || $[20] !== currentViewId || $[21] !== filtersStateStore || $[22] !== initialFilter || $[23] !== objectType || $[24] !== prevViewId || $[25] !== t7 || $[26] !== t8) {
    t9 = [currentUIMode, currentView, currentViewId, t7, t8, filtersStateStore, initialFilter, objectType, prevViewId];
    $[18] = currentUIMode;
    $[19] = currentView;
    $[20] = currentViewId;
    $[21] = filtersStateStore;
    $[22] = initialFilter;
    $[23] = objectType;
    $[24] = prevViewId;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  useEffect(t6, t9);
  const prevUIMode = usePrevious(currentUIMode);
  let t10;
  let t11;
  if ($[28] !== currentUIMode || $[29] !== filtersStateStore || $[30] !== initialFilter || $[31] !== prevUIMode) {
    t10 = () => {
      if (prevUIMode !== currentUIMode && currentUIMode === UI_MODE.FOLDERS) {
        filtersStateStore.getState().initializeFiltersState({
          currentView: undefined,
          uiMode: currentUIMode,
          initialFilter
        });
      }
    };
    t11 = [currentUIMode, filtersStateStore, initialFilter, prevUIMode];
    $[28] = currentUIMode;
    $[29] = filtersStateStore;
    $[30] = initialFilter;
    $[31] = prevUIMode;
    $[32] = t10;
    $[33] = t11;
  } else {
    t10 = $[32];
    t11 = $[33];
  }
  useEffect(t10, t11);
  let t12;
  if ($[34] !== currentUIMode || $[35] !== filtersStateStore) {
    t12 = effectEvent => {
      filtersStateStore.getState().updateInitialFilters({
        initialFilter: effectEvent.initialFilter,
        uiMode: currentUIMode
      });
    };
    $[34] = currentUIMode;
    $[35] = filtersStateStore;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleInitialFilterChange = useEventCallback(t12);
  let t13;
  if ($[37] !== filtersStateStore) {
    t13 = effectEvent_0 => {
      filtersStateStore.getState().setQuery(effectEvent_0.query);
    };
    $[37] = filtersStateStore;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const handleQueryChange = useEventCallback(t13);
  const initialFilterRef = useRef(initialFilter);
  let t14;
  let t15;
  if ($[39] !== handleInitialFilterChange || $[40] !== handleQueryChange || $[41] !== initialFilter) {
    t14 = () => {
      const hasInitialFilter = Boolean(initialFilterRef.current && initialFilter);
      const nonQueryFiltersAreEqual = isEqual(omit(["query"], initialFilterRef.current || {}), omit(["query"], initialFilter || {}));
      const queryIsDifferent = Boolean(initialFilterRef.current && initialFilter && initialFilterRef.current.query !== initialFilter.query);
      const hasOnlyQueryChanged = hasInitialFilter && nonQueryFiltersAreEqual && queryIsDifferent;
      if (hasOnlyQueryChanged) {
        var _initialFilter$query;
        handleQueryChange({
          query: (_initialFilter$query = initialFilter === null || initialFilter === void 0 ? void 0 : initialFilter.query) !== null && _initialFilter$query !== void 0 ? _initialFilter$query : ""
        });
      } else {
        if (!nonQueryFiltersAreEqual) {
          handleInitialFilterChange({
            initialFilter
          });
        }
      }
      initialFilterRef.current = initialFilter;
    };
    t15 = [handleInitialFilterChange, handleQueryChange, initialFilter];
    $[39] = handleInitialFilterChange;
    $[40] = handleQueryChange;
    $[41] = initialFilter;
    $[42] = t14;
    $[43] = t15;
  } else {
    t14 = $[42];
    t15 = $[43];
  }
  useEffect(t14, t15);
  let t16;
  if ($[44] !== currentUIMode || $[45] !== currentView || $[46] !== filtersStateStore || $[47] !== initialFilter) {
    t16 = effectEvent_1 => {
      filtersStateStore.getState().consumeQueryParams({
        urlParams: effectEvent_1.urlParams,
        currentView,
        uiMode: currentUIMode,
        initialFilter
      });
    };
    $[44] = currentUIMode;
    $[45] = currentView;
    $[46] = filtersStateStore;
    $[47] = initialFilter;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const handleConsumeQueryParams = useEventCallback(t16);
  let t17;
  let t18;
  if ($[49] !== currentView || $[50] !== filtersStateStore || $[51] !== handleConsumeQueryParams || $[52] !== hasViews || $[53] !== urlParams) {
    t17 = () => {
      const isLoadingOrInvalidView = hasViews && !currentView;
      if (isLoadingOrInvalidView || !urlParams || filtersStateStore.getState().hasUsedQueryParams) {
        return;
      }
      handleConsumeQueryParams({
        urlParams
      });
    };
    t18 = [currentView, filtersStateStore, handleConsumeQueryParams, hasViews, urlParams];
    $[49] = currentView;
    $[50] = filtersStateStore;
    $[51] = handleConsumeQueryParams;
    $[52] = hasViews;
    $[53] = urlParams;
    $[54] = t17;
    $[55] = t18;
  } else {
    t17 = $[54];
    t18 = $[55];
  }
  useEffect(t17, t18);
  let t19;
  if ($[56] !== filtersStateStore) {
    t19 = propertyName => filtersStateSelectors.selectHasPropertyFilter(filtersStateStore.getState(), {
      propertyName
    });
    $[56] = filtersStateStore;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  const hasPropertyFilter = t19;
  let t20;
  if ($[58] !== hasBUNavIntegration || $[59] !== hiddenFilterProp) {
    t20 = state => filtersStateSelectors.selectFilterGroups(state, {
      hiddenFilter: hiddenFilterProp,
      hasBUNavIntegration
    });
    $[58] = hasBUNavIntegration;
    $[59] = hiddenFilterProp;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  const filterGroups = useStoreWithEqualityFn(filtersStateStore, t20, shallow);
  let t21;
  if ($[61] !== hasBUNavIntegration) {
    t21 = state_0 => filtersStateSelectors.selectQuickFilters(state_0, {
      hasBUNavIntegration
    });
    $[61] = hasBUNavIntegration;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const quickFilters = useStoreWithEqualityFn(filtersStateStore, t21, shallow);
  let t22;
  if ($[63] !== calendarProps || $[64] !== currentUIMode || $[65] !== folderId || $[66] !== hasBUNavIntegration || $[67] !== hiddenFilterProp) {
    bb0: {
      const withHiddenBUFilter = updateHiddenFilterForBUNav({
        hiddenFilter: hiddenFilterProp,
        hasBUNavIntegration
      });
      const withHiddenFolderFilter = updateHiddenFilterForFolder({
        folderId,
        hiddenFilter: withHiddenBUFilter,
        isFoldersMode: currentUIMode === UI_MODE.FOLDERS
      });
      if (!calendarProps || currentUIMode !== UI_MODE.CALENDAR) {
        t22 = withHiddenFolderFilter;
        break bb0;
      }
      t22 = updateHiddenFilterForCalendarDisplayDate({
        displayTimestamp: calendarProps.displayTimestamp,
        endPropertyName: calendarProps.endPropertyName,
        hiddenFilter: withHiddenFolderFilter,
        startPropertyName: calendarProps.startPropertyName,
        viewType: calendarProps.use
      });
    }
    $[63] = calendarProps;
    $[64] = currentUIMode;
    $[65] = folderId;
    $[66] = hasBUNavIntegration;
    $[67] = hiddenFilterProp;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const hiddenFilter = t22;
  const deletedObjects = useStore(filtersStateStore, _temp4);
  let t23;
  bb1: {
    if (!currentView) {
      t23 = false;
      break bb1;
    }
    let t24;
    if ($[69] !== currentView.filterGroups || $[70] !== currentView.quickFilters || $[71] !== deletedObjects || $[72] !== filterGroups || $[73] !== hasBUNavIntegration || $[74] !== hiddenFilter || $[75] !== quickFilters) {
      const now = Date.now();
      const getNow = () => now;
      const currentAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups,
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters,
        getNow
      });
      const viewAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups: updateFilterGroupsForBUNav({
          filterGroups: currentView.filterGroups,
          hasBUNavIntegration
        }),
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters: updateQuickFiltersForBUNav({
          quickFilters: currentView.quickFilters,
          hasBUNavIntegration
        }),
        getNow
      });
      t24 = areSameFilterGroups(currentAggregatedFilterGroups, viewAggregatedFilterGroups);
      $[69] = currentView.filterGroups;
      $[70] = currentView.quickFilters;
      $[71] = deletedObjects;
      $[72] = filterGroups;
      $[73] = hasBUNavIntegration;
      $[74] = hiddenFilter;
      $[75] = quickFilters;
      $[76] = t24;
    } else {
      t24 = $[76];
    }
    t23 = t24;
  }
  const isFilterFromCustomView = t23;
  let t24;
  if ($[77] !== filtersStateStore) {
    t24 = (stateUpdate, options) => {
      filtersStateStore.getState().setFilters(stateUpdate, {
        source: (options === null || options === void 0 ? void 0 : options.source) || "other"
      });
    };
    $[77] = filtersStateStore;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  const onSetFilters = t24;
  let t25;
  if ($[79] !== filtersStateStore) {
    t25 = update => {
      filtersStateStore.getState().setDeletedObjects(update);
    };
    $[79] = filtersStateStore;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  const onSetDeletedObjects = t25;
  const filtersState = useStore(filtersStateStore, _temp5);
  const filterStateSource = useStore(filtersStateStore, _temp6);
  let t26;
  if ($[81] !== filterGroups || $[82] !== filtersState || $[83] !== quickFilters) {
    t26 = Object.assign({}, filtersState, {
      filterGroups,
      quickFilters
    });
    $[81] = filterGroups;
    $[82] = filtersState;
    $[83] = quickFilters;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== deletedObjects || $[86] !== filterStateSource || $[87] !== hasPropertyFilter || $[88] !== hiddenFilter || $[89] !== isFilterFromCustomView || $[90] !== onSetDeletedObjects || $[91] !== onSetFilters || $[92] !== t26) {
    t27 = {
      deletedObjects,
      filters: t26,
      filterStateSource,
      hasPropertyFilter,
      hiddenFilter,
      isFilterFromCustomView,
      onSetDeletedObjects,
      onSetFilters
    };
    $[85] = deletedObjects;
    $[86] = filterStateSource;
    $[87] = hasPropertyFilter;
    $[88] = hiddenFilter;
    $[89] = isFilterFromCustomView;
    $[90] = onSetDeletedObjects;
    $[91] = onSetFilters;
    $[92] = t26;
    $[93] = t27;
  } else {
    t27 = $[93];
  }
  const value = t27;
  return value;
}
function _temp6(s_3) {
  return s_3.filterStateSource;
}
function _temp5(s_2) {
  return s_2.filtersState;
}
function _temp4(state_1) {
  return state_1.deletedObjects;
}
function _temp3(s_1) {
  return s_1.shouldSyncWithSessionStorage;
}
function _temp2(s_0) {
  return s_0.filtersState.quickFilters;
}
function _temp(s) {
  return s.filtersState.filterGroups;
}